import { fetchWithAuth } from './fetchWithAuth';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE!;

export interface ChainOption {
    networkId: number;
    chainId: number;
    name: string;
    title: string | null;
    shortName: string | null;
    nativeCurrencySymbol: string | null;
    explorerUrl: string | null;
    rpcUrls: string[];
}

export interface TenantSummary {
    id: number;
    name: string;
    slug: string;
    billingProvider: 'MANUAL' | 'STRIPE';
    billingStatus: 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'INCOMPLETE' | 'UNPAID' | null;
    billingGraceEndsAt: string | null;
    cancelAtPeriodEnd: boolean;
    subscriptionPlanId: number | null;
    subscriptionExpiresAt: string | null;
    subscriptionPlan: { id: number; code: string; name: string } | null;
    contractAddress: string | null;
    signerPublicAddress: string | null;
    networkId: number | null;
    chainId: number | null;
    chainName: string | null;
    chainExplorerUrl: string | null;
    contractExplorerUrl: string | null;
    signerExplorerUrl: string | null;
    rpcUrl: string | null;
    verificationBaseUrl: string | null;
    verificationTemplateId: string | null;
    verificationTemplateConfig: Record<string, unknown> | null;
    shortenerApi: string | null;
    shortenerDomainId: number | null;
    shortenerDomain: { id: number; label: string; domain: string } | null;
    logoUrl: string | null;
    tintElements: string | null;
    tintText: string | null;
    tintBackground: string | null;
    linkedInOrganizationId: string | null;
    personalCertificateEmails: boolean;
    certificateCount: number;
    userCount: number;
    subscription?: {
        planId: number;
        planCode: string;
        planName: string;
        currency: string;
        baseMonthlyPrice: number;
        annualCommitmentPrice: number;
        includedCertificates: number;
        purchasedCertificates: number;
        certificateLimit: number;
        maxCertificates: number;
        includedSeats: number;
        purchasedSeats: number;
        seatLimit: number;
        maxSeats: number;
        additionalCertificatePrice: number;
        additionalSeatMonthlyPrice: number;
        usedCertificates: number;
        usedSeats: number;
        additionalCertificates: number;
        additionalSeats: number;
        estimatedMonthlyTotal: number;
        estimatedAdditionalCertificatesTotal: number;
    } | null;
    createdAt: string;
    updatedAt: string;
}

export interface SubscriptionPlan {
    id: number;
    code: string;
    name: string;
    isVisibleToTenants: boolean;
    currency: string;
    baseMonthlyPrice: number;
    annualCommitmentPrice: number;
    includedCertificates: number;
    maxCertificates: number;
    includedSeats: number;
    maxSeats: number;
    additionalCertificatePrice: number;
    additionalSeatMonthlyPrice: number;
    stripeMonthlyPriceId?: string | null;
    stripeAnnualPriceId?: string | null;
    stripeSeatPriceId?: string | null;
    stripeCertificateTopupPriceId?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ApprovedCurrency {
    code: string;
    name: string | null;
}

export interface PlatformUserSummary {
    id: number;
    email: string;
    role: 'USER' | 'ADMIN' | 'OPERATOR';
    isTwoFactorEnabled: boolean;
    isEmailVerified: boolean;
    membershipCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface PlatformUserDetail extends PlatformUserSummary {
    memberships: Array<{
        id: number;
        role: 'USER' | 'ADMIN' | 'OPERATOR';
        firstName: string | null;
        lastName: string | null;
        phoneNumber: string | null;
        jobTitle: string | null;
        createdAt: string;
        updatedAt: string;
        tenant: { id: number; name: string; slug: string };
    }>;
}

export interface TenantUserSummary {
    userId: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
    jobTitle: string | null;
    userRole: 'USER' | 'ADMIN' | 'OPERATOR';
    membershipRole: 'USER' | 'ADMIN' | 'OPERATOR';
    isTwoFactorEnabled: boolean;
    isEmailVerified: boolean;
    logoUrl: string | null;
    joinedAt: string;
    lastUpdatedAt: string;
}

export interface MasterWalletSummary {
    id: number;
    label: string;
    networkId: number;
    rpcUrl: string | null;
    address: string;
    createdAt: string;
    updatedAt: string;
}

export interface ShortenerDomainSummary {
    id: number;
    label: string;
    domain: string;
    apiUrl: string;
    originHeader: string | null;
    isDefault: boolean;
    _count?: { tenants: number };
    createdAt: string;
    updatedAt: string;
}

export interface TenantContextOverview {
    id: number;
    name: string;
    slug: string;
    contractAddress: string | null;
    networkId: number | null;
    chainId: number | null;
    chainName: string | null;
    certificateCount: number;
    userCount: number;
    verificationBaseUrl: string | null;
    verificationTemplateId: string | null;
    subscription: {
        planId: number;
        planCode: string;
        planName: string;
        includedCertificates: number;
        purchasedCertificates: number;
        certificateLimit: number;
        includedSeats: number;
        purchasedSeats: number;
        seatLimit: number;
        maxSeats: number;
        usedCertificates: number;
        usedSeats: number;
    } | null;
}

export interface SubscriptionHealthDto {
    status: 'active' | 'grace_period' | 'canceled_active' | 'expired' | 'blocked';
    billingStatus: 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'INCOMPLETE' | 'UNPAID' | null;
    cancelAtPeriodEnd: boolean;
    subscriptionExpiresAt: string | null;
    billingGraceEndsAt: string | null;
    planName: string | null;
}

export async function fetchSubscriptionHealth(): Promise<SubscriptionHealthDto> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/subscription-health`);
    if (!res.ok) {
        throw new Error('Failed to fetch subscription health');
    }
    return res.json();
}

export async function fetchTenants(): Promise<TenantSummary[]> {
    const res = await fetchWithAuth(`${API_BASE}/tenants`);
    if (!res.ok) {
        throw new Error('Failed to fetch tenants');
    }
    return res.json();
}

export async function fetchTenantUsers(tenantId: number): Promise<TenantUserSummary[]> {
    const res = await fetchWithAuth(`${API_BASE}/tenants/${tenantId}/users`);
    if (!res.ok) {
        throw new Error('Failed to fetch tenant users');
    }
    return res.json();
}

export async function deployTenantContract(tenantId: number): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenants/${tenantId}/deploy-contract`, {
        method: 'POST',
    });

    if (!res.ok) {
        let message = 'Failed to deploy tenant contract';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore parse errors
        }
        throw new Error(message);
    }

    return res.json();
}

export async function fetchMasterWallets(): Promise<MasterWalletSummary[]> {
    const res = await fetchWithAuth(`${API_BASE}/admin/master-wallets`);
    if (!res.ok) {
        throw new Error('Failed to fetch master wallets');
    }
    return res.json();
}

export async function upsertMasterWallet(payload: {
    label: string;
    networkId: number;
    rpcUrl: string;
    privateKey: string;
}): Promise<MasterWalletSummary> {
    const res = await fetchWithAuth(`${API_BASE}/admin/master-wallets`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to store master wallet';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function fetchChainOptions(query?: string): Promise<ChainOption[]> {
    const params = query ? `?q=${encodeURIComponent(query)}` : '';
    const res = await fetchWithAuth(`${API_BASE}/tenants/chain-options${params}`);
    if (!res.ok) {
        throw new Error('Failed to retrieve chain list');
    }
    return res.json();
}

export async function updateTenantChain(
    tenantId: number,
    payload: { networkId: number; rpcUrl?: string },
): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenants/${tenantId}/chain`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update tenant chain';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function updateTenantSubscriptionPlan(
    tenantId: number,
    subscriptionPlanId: number | null,
    expiresAt?: string | null,
): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenants/${tenantId}/subscription`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscriptionPlanId, expiresAt }),
    });

    if (!res.ok) {
        let message = 'Failed to update subscription';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function updateTenantSubscriptionAddons(
    tenantId: number,
    payload: { seatsToAdd?: number; certificatesToAdd?: number },
): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenants/${tenantId}/subscription/addons`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update tenant add-ons';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function fetchSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const res = await fetchWithAuth(`${API_BASE}/admin/subscription-plans`);
    if (!res.ok) {
        throw new Error('Failed to fetch subscription plans');
    }
    return res.json();
}

export async function fetchApprovedCurrencies(): Promise<ApprovedCurrency[]> {
    const res = await fetchWithAuth(`${API_BASE}/admin/subscription-plans/currencies`);
    if (!res.ok) {
        throw new Error('Failed to fetch approved currencies');
    }
    return res.json();
}

export async function fetchPlatformUsers(): Promise<PlatformUserSummary[]> {
    const res = await fetchWithAuth(`${API_BASE}/admin/users`);
    if (!res.ok) {
        throw new Error('Failed to fetch platform users');
    }
    return res.json();
}

export async function fetchPlatformUser(userId: number): Promise<PlatformUserDetail> {
    const res = await fetchWithAuth(`${API_BASE}/admin/users/${userId}`);
    if (!res.ok) {
        let message = 'Failed to fetch user';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }
    return res.json();
}

export async function createSubscriptionPlan(payload: Omit<SubscriptionPlan, 'id' | 'createdAt' | 'updatedAt'>) {
    const res = await fetchWithAuth(`${API_BASE}/admin/subscription-plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to create subscription plan';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json() as Promise<SubscriptionPlan>;
}

export async function updateSubscriptionPlan(
    planId: number,
    payload: Partial<Omit<SubscriptionPlan, 'id' | 'createdAt' | 'updatedAt'>>,
) {
    const res = await fetchWithAuth(`${API_BASE}/admin/subscription-plans/${planId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update subscription plan';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json() as Promise<SubscriptionPlan>;
}

export async function deleteSubscriptionPlan(planId: number) {
    const res = await fetchWithAuth(`${API_BASE}/admin/subscription-plans/${planId}`, {
        method: 'DELETE',
    });

    if (!res.ok) {
        let message = 'Failed to delete subscription plan';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json() as Promise<{ ok: true }>;
}

export async function fetchTenantOverview(): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/overview`);
    if (!res.ok) {
        throw new Error('Failed to load tenant overview');
    }
    return res.json();
}

export async function updateTenantBranding(payload: {
    tintElements: string | null;
    tintText: string | null;
    tintBackground?: string | null;
}): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/branding`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update tenant branding';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function purchaseTenantSeats(payload: { seats: number }): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/subscription/seats`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to purchase seats';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function purchaseTenantAddons(payload: {
    seatsToAdd?: number;
    certificatesToAdd?: number;
}): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/subscription/addons`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to purchase add-ons';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function fetchTenantContextOverview(): Promise<TenantContextOverview> {
    const res = await fetchWithAuth(`${API_BASE}/users/tenant-overview`);
    if (!res.ok) {
        throw new Error('Failed to load tenant overview');
    }
    return res.json();
}

export async function updateTenantVerificationTemplate(payload: {
    verificationTemplateId: string | null;
}): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/verification-template`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update verification template';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function updateTenantVerificationTemplateConfig(payload: {
    templateId: string;
    backgroundColor?: string | null;
    logoUrl?: string | null;
}): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/verification-template-config`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update verification template config';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function fetchTenantMembersForAdmin(): Promise<TenantUserSummary[]> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/users`);
    if (!res.ok) {
        throw new Error('Failed to load tenant members');
    }
    return res.json();
}

export async function updateTenantMemberRole(
    userId: number,
    role: 'USER' | 'ADMIN' | 'OPERATOR',
): Promise<TenantUserSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
    });

    if (!res.ok) {
        let message = 'Failed to update member role';
        try {
            const data = await res.json();
            message = data?.message ?? message;
        } catch {
            // ignore parse failure
        }
        throw new Error(message);
    }

    return res.json();
}

export async function removeTenantMember(userId: number): Promise<void> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/users/${userId}`, {
        method: 'DELETE',
    });

    if (!res.ok) {
        let message = 'Failed to remove member';
        try {
            const data = await res.json();
            message = data?.message ?? message;
        } catch {
            // ignore parse failure
        }
        throw new Error(message);
    }
}

export async function updateTenantMemberProfile(
    userId: number,
    payload: {
        firstName?: string | null;
        lastName?: string | null;
        phoneNumber?: string | null;
        jobTitle?: string | null;
    },
): Promise<TenantUserSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenant-admin/users/${userId}/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update member profile';
        try {
            const data = await res.json();
            message = data?.message ?? message;
        } catch {
            // ignore parse failure
        }
        throw new Error(message);
    }

    return res.json();
}

export async function fetchShortenerDomains(): Promise<ShortenerDomainSummary[]> {
    const res = await fetchWithAuth(`${API_BASE}/admin/shortener-domains`);
    if (!res.ok) {
        throw new Error('Failed to fetch shortener domains');
    }
    return res.json();
}

export async function createShortenerDomain(payload: {
    label: string;
    domain: string;
    apiUrl: string;
    originHeader?: string;
    isDefault?: boolean;
}): Promise<ShortenerDomainSummary> {
    const res = await fetchWithAuth(`${API_BASE}/admin/shortener-domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to create shortener domain';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function updateShortenerDomain(
    id: number,
    payload: Partial<{
        label: string;
        domain: string;
        apiUrl: string;
        originHeader: string | null;
        isDefault: boolean;
    }>,
): Promise<ShortenerDomainSummary> {
    const res = await fetchWithAuth(`${API_BASE}/admin/shortener-domains/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update shortener domain';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function deleteShortenerDomain(id: number): Promise<{ ok: true }> {
    const res = await fetchWithAuth(`${API_BASE}/admin/shortener-domains/${id}`, {
        method: 'DELETE',
    });

    if (!res.ok) {
        let message = 'Failed to delete shortener domain';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function updateTenantCertificateEmailOptions(
    tenantId: number,
    payload: {
        linkedInOrganizationId?: string | null;
        personalCertificateEmails?: boolean;
    },
): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenants/${tenantId}/certificate-email-options`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        let message = 'Failed to update certificate email options';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}

export async function updateTenantShortenerDomain(
    tenantId: number,
    shortenerDomainId: number | null,
): Promise<TenantSummary> {
    const res = await fetchWithAuth(`${API_BASE}/tenants/${tenantId}/shortener-domain`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortenerDomainId }),
    });

    if (!res.ok) {
        let message = 'Failed to update tenant shortener domain';
        try {
            const body = await res.json();
            message = body?.message ?? message;
        } catch {
            // ignore
        }
        throw new Error(message);
    }

    return res.json();
}
